import { Address, SocialNetwork, SelectOption, Logo, Statistic } from 'Types';

export const enum AgentApproveStatus {
	pending = 0,
	approved = 1,
	rejected = 2,
	canceled = 3,
	inactive = 4
}

export const enum AgentPartnerApprovalStatus {
	autoApproved = 1,
	approved = 2,
	pending = 3,
	rejected = 4,
	autoRejected = 5
}

export enum CommunityPartnerProfileStatus {
	Registration = 0,
	PendingApproval = 1,
	Active = 2,
	Rejected = 3
}

export interface CommunityPartnerBankAccount {
	payout: boolean;
	ownerName: string;
	iban: string;
	bic: string;
	climateChange: boolean;
}

export interface CommunityPartnerHeader {
	headerImage: Logo;
	logo: Logo;
	headerTitle: string;
	slogan: string;
	description: string;
	themeColor: number;
	address: Address;
}

export interface CommunityPartnerProfile {
	id: string;
	communityPartnerId: string;
	header: CommunityPartnerHeader;
	version: number;
	benefitList?: string[];
	socialNetworks: SocialNetwork[];
	auditInfo?: {
		createdBy: string;
		createdAt: string;
		updatedBy: string;
		updatedAt: string;
	};
	contactPerson: {
		address: string;
		description: string;
		email: string;
		fullName: string;
		image: Logo;
		phoneNumber: string;
		position: string;
	};
	statistics: {
		enabled: boolean;
	};
	statisticItems: Statistic[];
	employee: {
		enabled: boolean;
		headline: string;
	};
	jobs: {
		enabled: boolean;
		headline: string;
	};
	jobAds: {
		enabled: boolean;
		headline: string;
		description: number;
	};
	internships: {
		enabled: boolean;
		headline: string;
	};
	benefits: {
		enabled: boolean;
		headline: string;
		description: string | number;
	};
	departments: {
		enabled: boolean;
		headline: string;
	};
	members: {
		enabled: boolean;
		headline: string;
	};
	becomePartner: {
		enabled: boolean;
		headline: string;
		description: string;
		image: Logo;
	};
	events: {
		enabled: boolean;
		headline: string;
	};
	partners: {
		enabled: boolean;
		headline: string;
		description: string;
	};
	areas: {
		enabled: boolean;
		headline: string;
		description: string;
	};
	jobSearch: {
		enabled: boolean;
		headline: string;
		jobFilter: number;
	};
	commissionGroupInfo: {
		commissionGroupId: null | string;
		title: null | string;
		premiumLabel: null | string;
		isActive: boolean;
		isPremium: boolean;
	};
	topJobs: {
		enabled: boolean;
		headline: string;
	};
	slugUrl: string;
}

export interface CommunityPartnerCommissionLevelMe {
	ActiveSubscriptionCount: number;
	CommissionLevelInfo: {
		CommissionLevelId: {
			value: string;
		};
		Title: string;
		MinActiveAbo: number;
		MaxActiveAbo: number;
		CommissionPercent: number;
		CommissionCost: number;
	};
}

export interface CommunityPartnerMe {
	id: string;
	communityPartnerProfileId: string;
	associateCommunityPartnerId: any;
	communityPartnerTypeCode: any;
	partnerClass: number;
	commissionGroupId: string;
	tenantId: string;
	logo: {
		url: string;
		thumbnail: string;
	};
	slugUrl: string;
	businessName: string;
	firstName: string;
	lastName: string;
	mainEmailAddress: string;
	hrEmailAddress: string;
	emailVerificationCode: string;
	emailIsVerified: boolean;
	emailVerifiedAt: string;
	regionCode: string;
	address: {
		name: string;
		countryName: string;
		countryIso2: string;
		countryIso3: string;
		state: string;
		city: string;
		postalCode: string;
		street: string;
		houseNumber: string;
		location: {
			longitude: number;
			latitude: number;
		};
	};
	status: number;
	taxNumber: string;
	webSite: string;
	additionalPlanPrice: number;
	publishInfo: {
		lastUpdatedAt: string;
		status: number;
		version: number;
	};
	bankAccount: {
		payout: boolean;
		ownerName: string;
		iban: string;
		bic: string;
		climateChange: boolean;
	};
	smallBusiness: boolean;
	isAutoApprove: boolean;
	slogan: string;
	description: string;
	commissionGroupInfo: {
		isPremium: boolean;
		premiumLabel: string;
	};
	activeSubscriptionCount: number;
	sectorCode: SelectOption | undefined | null | string;
	businessSectorInfo: {
		businessSectorId: string;
		title: string;
		translateTitles: [
			{
				language: string;
				value: string;
			}
		];
	};
}

export interface CommunityPartnerProfileData extends CommunityPartnerMe {
	sectorCode: SelectOption | undefined | null;
}

export interface CommunityPartnerType {
	id: string;
	title: string;
	associateCommunityPartnerId: string;
	description: string;
	code: string;
	translateTitles: [
		{
			language: string;
			value: string;
		}
	];
	active: boolean;
}

export interface CommunityPartnerAssociation {
	businessName: string;
	id: string;
}

export interface CommunityPartnerSettings {
	communityPartnerTypeCode: any;
	associateCommunityPartnerId: any;
}

export interface CommunityPartnerArea {
	businessName: string;
	id: string;
	logo: Logo;
}

export interface CommunityPartnerPartner {
	id: string;
	thumbnailLogo: string;
	businessName: string;
	mainEmailAddress: string;
	status: number;
	slugUrl: string;
	partnershipRequestsInfo: any;
	registrationInfo: {
		startedAt: string;
		lastUpdatedAt: string;
		status: number;
		referralCommunityPartnerId: {
			value: string;
		};
	};
	publishInfo: {
		lastUpdatedAt: string;
		status: number;
		version: number;
	};
	logo: Logo;
	slogan: string;
	description: string;
}

export interface Member {
	id: string;
	phoneNumber: string;
	description: string;
	isActive: boolean;
	fullName: string;
	email: string;
	showInHomePage: boolean;
	image: { thumbnail: string; url: string } | null;
	jobPosition: string;
}

export interface Department {
	id: string;
	title: string;
	description: string;
	isActive: boolean;
	image: Logo;
	sortOrder: number;
}

export interface SubscriptionPlanContactPersonDetails {
	fullName: string;
	description: string;
	avatarUrl: string;
	published: boolean;
}

export interface PartnersInsight {
	totalViewAllTime: number;
	totalApplyAllTime: number;
	monthlyInsights: {
		month: number;
		year: number;
		totalView: number;
		totalApply: number;
	}[];
}
